import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  ChevronRight, 
  Home, 
  User, 
  Blocks, 
  MessageSquare, 
  Phone,
  ExternalLink,
  ArrowUp,
  Users,
  Briefcase
} from 'lucide-react';

const Header = () => {
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const menuItems = [
    { href: '#about', label: 'Sobre', icon: User },
    { href: '#career', label: 'Carreira', icon: Briefcase },
    { href: '#community', label: 'Comunidade', icon: Users },
    { href: '#projects', label: 'Projetos', icon: Blocks },
    { href: '#contact', label: 'Contato', icon: Phone }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setShowScrollTop(window.scrollY > 500);

      const sections = menuItems.map(item => item.href.substring(1));
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      setActiveSection(currentSection || '');
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
          isScrolled || isMobileMenuOpen
            ? 'bg-black border-b border-blue-900/50' 
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a 
              href="#" 
              className="relative group px-4 py-2"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setIsMobileMenuOpen(false);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                VV
              </span>
              <motion.span 
                className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-blue-400/0 via-blue-400/50 to-blue-400/0"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.5 }}
              />
            </motion.a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-1">
              <motion.div 
                className="flex bg-blue-900/50 backdrop-blur-sm rounded-full p-1.5"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {menuItems.map(({ href, label, icon: Icon }) => (
                  <motion.a
                    key={href}
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(href.substring(1));
                    }}
                    className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-full flex items-center gap-2 ${
                      activeSection === href.substring(1)
                        ? 'text-white bg-gradient-to-r from-blue-500 to-blue-700'
                        : 'text-blue-300 hover:text-white hover:bg-blue-800/50'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {activeSection === href.substring(1) && (
                      <motion.span 
                        className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/20 to-blue-700/20 -z-10"
                        layoutId="activeSection"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </motion.a>
                ))}
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden relative z-[9998] p-2 rounded-lg bg-blue-900/50 backdrop-blur-sm text-blue-300 hover:text-blue-400 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="h-6 w-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="h-6 w-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </nav>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/90 backdrop-blur-xl md:hidden z-[9996]"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              
              {/* Menu Panel */}
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="fixed right-0 top-0 h-full w-80 bg-black border-l border-blue-900/50 md:hidden z-[9997]"
              >
                <div className="flex flex-col p-6 mt-20">
                  <motion.a
                    href="#"
                    className="flex items-center justify-between p-4 text-blue-300 hover:text-blue-400 hover:bg-blue-900/20 rounded-xl transition-all duration-300 mb-6 group"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setIsMobileMenuOpen(false);
                    }}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-blue-900/20">
                        <Home className="h-5 w-5 text-blue-400" />
                      </div>
                      <span className="font-medium">Início</span>
                    </div>
                    <ChevronRight className="h-4 w-4 opacity-50 group-hover:translate-x-1 transition-transform" />
                  </motion.a>
                  
                  <div className="space-y-2">
                    {menuItems.map(({ href, label, icon: Icon }) => (
                      <motion.a
                        key={href}
                        href={href}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToSection(href.substring(1));
                        }}
                        className={`flex items-center justify-between p-4 rounded-xl transition-all duration-300 group ${
                          activeSection === href.substring(1)
                            ? 'text-blue-400 bg-blue-900/20'
                            : 'text-blue-300 hover:text-blue-400 hover:bg-blue-900/10'
                        }`}
                        whileHover={{ x: 4 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            activeSection === href.substring(1)
                              ? 'bg-blue-900/30'
                              : 'bg-blue-900/20'
                          }`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="font-medium">{label}</span>
                        </div>
                        <ChevronRight className="h-4 w-4 opacity-50 group-hover:translate-x-1 transition-transform" />
                      </motion.a>
                    ))}
                  </div>

                  <div className="mt-8 p-4 rounded-xl bg-gradient-to-r from-blue-900/20 to-blue-700/20 border border-blue-900/20">
                    <h4 className="text-sm font-medium text-blue-400 mb-2">Disponível para:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm text-blue-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Freelance
                      </li>
                      <li className="flex items-center gap-2 text-sm text-blue-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Mentoria
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </header>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-8 right-8 p-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/20 z-40"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;